import React, { createContext, useContext, useState, useEffect } from 'react';
import { getApiUrl } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Use XMLHttpRequest instead of fetch to avoid "Body is disturbed" issues
const makeRequest = (method, url, data = null, headers = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    
    // Set headers
    xhr.setRequestHeader('Accept', 'application/json');
    Object.keys(headers).forEach(key => {
      xhr.setRequestHeader(key, headers[key]);
    });
    
    if (data) {
      xhr.setRequestHeader('Content-Type', 'application/json');
    }
    
    xhr.onload = function() {
      try {
        const response = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ ok: true, status: xhr.status, data: response });
        } else {
          resolve({ ok: false, status: xhr.status, data: response });
        }
      } catch (e) {
        reject(new Error('Invalid JSON response'));
      }
    };
    
    xhr.onerror = function() {
      reject(new Error('Network error'));
    };
    
    xhr.send(data ? JSON.stringify(data) : null);
  });
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('adminToken'));

  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = localStorage.getItem('adminToken');
      const apiUrl = getApiUrl();
      
      if (savedToken) {
        try {
          const result = await makeRequest('GET', `${apiUrl}/api/auth/me`, null, {
            'Authorization': `Bearer ${savedToken}`
          });
          if (result.ok) {
            setUser(result.data);
            setToken(savedToken);
          } else {
            localStorage.removeItem('adminToken');
            setToken(null);
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('adminToken');
          setToken(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    const apiUrl = getApiUrl();
    console.log('Login attempt to:', `${apiUrl}/api/auth/login`);
    
    try {
      const result = await makeRequest('POST', `${apiUrl}/api/auth/login`, { email, password });
      console.log('Login response:', result.status, result.data);
      
      if (!result.ok) {
        throw new Error(result.data.detail || 'Login failed');
      }

      localStorage.setItem('adminToken', result.data.access_token);
      setToken(result.data.access_token);
      setUser({ email });
      return result.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const setup = async (email, password) => {
    const apiUrl = getApiUrl();
    
    const result = await makeRequest('POST', `${apiUrl}/api/auth/setup`, { email, password });
    
    if (!result.ok) {
      throw new Error(result.data.detail || 'Setup failed');
    }

    localStorage.setItem('adminToken', result.data.access_token);
    setToken(result.data.access_token);
    setUser({ email });
    return result.data;
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    setup,
    logout,
    isAuthenticated: !!token
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
